import type { ProLayoutProps } from '@ant-design/pro-layout/es/ProLayout'
import { db, initializer } from '@app/icare/client/db'
import { installDevTools } from '@app/icare/client/devTools'
import { localQueryClient } from '@app/icare/client/localQueryClient'
import { startClientStatusRouteSync, sync } from '@app/icare/client/sync'
import { routeTree } from '@app/icare/routeTree.gen'
import { NotFoundErrorComponent } from '@pkg/ui/error'
import type { BreadcrumbLoaderData } from '@pkg/ui/hooks'
import type { RuntimePageContainerProps } from '@pkg/ui/layout/PageContainer'
import type { PageHeaderConfig } from '@pkg/ui/types/pageHeader'
import { environmentManager, type QueryClient } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { DefaultCatchBoundary } from './components/DefaultCatchBoundary'

export interface AppRouterContext {
  queryClient: QueryClient
}

export type AppRouter = ReturnType<typeof getRouter>

export let router: AppRouter

export function getRouter() {
  const internalRouter = createRouter({
    routeTree,
    context: { queryClient: localQueryClient },
    defaultPreload: 'intent',
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <NotFoundErrorComponent />,
    defaultPreloadStaleTime: 0,
    isServer: environmentManager.isServer(),

    hydrate: h => {
      // QueryClient is locally only. Remove everything from the hydration
      delete h?.dehydratedQueryClient
    },
  })
  if (!router) {
    router = internalRouter
    if (!environmentManager.isServer()) {
      startClientStatusRouteSync(router)
      installDevTools({
        database: db,
        navigate: router.navigate.bind(router),
        ready: initializer.waitForInit,
        sync,
      })
    }
  }
  return internalRouter
}

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }

  interface StaticDataRouteOption extends BreadcrumbLoaderData {
    layout?: ProLayoutProps
    header?: PageHeaderConfig
    pageContainer?: RuntimePageContainerProps
  }
}
